import { useState } from 'react';
import {
  DeleteButton,
  Empty,
  ErrorBanner,
  Field,
  InlineEdit,
  Loading,
  Meter,
  Modal,
  PageHead,
  ScaleInput,
} from '../components/ui';
import { api } from '../lib/api';
import {
  formatDate,
  humanise,
  maturityLabel,
  maturityName,
  maturityPercent,
  maturityToneClass,
  todayLocal,
} from '../lib/format';
import { useResource } from '../lib/hooks';
import { EosSyncButton } from './eos/EosSync';
import { Tabs } from './am/amUi';
import { OmlCompare } from './oml/OmlCompare';
import { QuestionEditor } from './oml/QuestionEditor';
import type {
  Assessment,
  AssessmentSummary,
  Criterion,
  Me,
  PlaybookDimension,
  PlaybookProcess,
} from '../lib/types';

/**
 * OML — Operational Maturity Level.
 *
 * The template (dimensions and criteria) is data, so this page renders
 * whatever the seed defines rather than hard-coding the questions.
 */
export function OmlPage() {
  const list = useResource<AssessmentSummary[]>('/assessments');
  /* Read here rather than threaded down from App, the same way the Account
     Management page does it: /me is already in the browser's cache by the time
     any page renders. */
  const me = useResource<Me>('/me');
  const isAdmin = me.data?.appRole === 'admin';
  const [openId, setOpenId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [editingQuestions, setEditingQuestions] = useState(false);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState<OmlImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  /* The list, or Compare over time (pages/oml/OmlCompare.tsx). */
  const [view, setView] = useState<'list' | 'compare'>('list');
  /* The assessment whose company, name and date are being edited. */
  const [editing, setEditing] = useState<AssessmentSummary | null>(null);

  /* An OML Assessment report saved from the EOS app, read in the browser and
     sent as text — the API reads it (routes/assessments.ts, POST /import). */
  const importReport = async (file: File) => {
    setImporting(true);
    setImportError(null);
    try {
      const html = await file.text();
      const result = await api.post<OmlImportResult>('/assessments/import', { html, fileName: file.name });
      setImported(result);
      list.reload();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Could not import the report');
    } finally {
      setImporting(false);
    }
  };

  if (openId !== null) {
    return <AssessmentDetail id={openId} onBack={() => { setOpenId(null); list.reload(); }} />;
  }

  /* Admin-gated in the API too — `requireAdmin` on the question routes is the
     rule; hiding the button is presentation. */
  if (editingQuestions && isAdmin) {
    return <QuestionEditor onBack={() => setEditingQuestions(false)} />;
  }

  return (
    <>
      <PageHead
        title="OML Assessment"
        lede="Operational maturity scored 1–5 across five dimensions. Run it on a cadence — the trend between assessments is worth more than any single score."
        actions={
          <>
            <EosSyncButton path="/assessments/sync" isAdmin={isAdmin} onSynced={list.reload} />
            {isAdmin && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setEditingQuestions(true)}
              >
                Edit questions
              </button>
            )}
            {/* A label around a hidden file input is a button that opens the
                file picker with no script of its own. */}
            <label className={`btn btn-secondary${importing ? ' is-disabled' : ''}`} aria-disabled={importing}>
              {importing ? 'Importing…' : 'Import report'}
              <input
                type="file"
                accept=".html,.htm,text/html"
                hidden
                disabled={importing}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) void importReport(file);
                }}
              />
            </label>
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              New assessment
            </button>
          </>
        }
      />

      {importError && <ErrorBanner message={importError} />}

      <Tabs<'list' | 'compare'>
        tabs={[
          { key: 'list', label: 'Assessments', count: list.data?.length },
          { key: 'compare', label: 'Compare over time' },
        ]}
        active={view}
        onChange={setView}
      />

      {view === 'compare' && <OmlCompare />}

      {view === 'list' && list.error && <ErrorBanner message={list.error} />}
      {view === 'list' && list.loading && <Loading />}

      {view === 'list' && list.data && list.data.length === 0 && (
        <Empty title="No assessments yet">
          <p>Start one to establish your baseline maturity level.</p>
        </Empty>
      )}

      {view === 'list' && list.data && list.data.length > 0 && (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Assessment</th>
                  <th>Company</th>
                  <th>Period</th>
                  <th>Conducted</th>
                  <th style={{ width: 200 }}>Progress</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.data.map((a) => {
                  const pct = a.CriteriaCount ? (a.ScoredCount / a.CriteriaCount) * 100 : 0;
                  return (
                    <tr key={a.AssessmentId}>
                      <td className="strong">{a.Title}</td>
                      <td>{a.Company ?? <span className="subtle">—</span>}</td>
                      <td>{a.PeriodLabel ?? <span className="subtle">—</span>}</td>
                      <td className="mono xs">{formatDate(a.ConductedOn)}</td>
                      <td>
                        <div className="meter">
                          <span className={pct < 50 ? 'low' : ''} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="xs muted mono">
                          {a.ScoredCount}/{a.CriteriaCount} scored
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${a.Status === 'complete' ? 'badge-good' : 'badge-neutral'}`}>
                          {humanise(a.Status)}
                        </span>
                      </td>
                      <td className="actions">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpenId(a.AssessmentId)}>
                          Open
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          title="Edit the company, name or date"
                          onClick={() => setEditing(a)}
                        >
                          Edit
                        </button>
                        <DeleteButton
                          what={a.Title}
                          onConfirm={() => void api.del(`/assessments/${a.AssessmentId}`).then(list.reload)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating && (
        <NewAssessment
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            list.reload();
            setOpenId(id);
          }}
        />
      )}

      {editing && (
        <EditAssessment
          assessment={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            list.reload();
          }}
        />
      )}

      {imported && (
        <ImportedReport
          result={imported}
          onClose={() => setImported(null)}
          onOpen={() => {
            setOpenId(imported.AssessmentId);
            setImported(null);
          }}
        />
      )}
    </>
  );
}

interface OmlImportResult {
  AssessmentId: number;
  title: string;
  conductedOn: string | null;
  imported: number;
  answers: number;
  average: number;
  reportAverage: number | null;
  averageMatches: boolean;
  unmatched: string[];
  disagreements: string[];
}

/** What an import did, including anything it could not match or check. */
function ImportedReport({
  result,
  onClose,
  onOpen,
}: {
  result: OmlImportResult;
  onClose: () => void;
  onOpen: () => void;
}) {
  const clean = result.averageMatches && result.unmatched.length === 0 && result.disagreements.length === 0;
  return (
    <Modal
      title="Report imported"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
          <button type="button" className="btn btn-primary" onClick={onOpen}>
            Open it
          </button>
        </>
      }
    >
      <p>
        <strong>{result.title}</strong>
        {result.conductedOn && <> · {formatDate(result.conductedOn)}</>}
      </p>
      <p className="small">
        {result.imported} of {result.answers} answers imported as a completed assessment. Average{' '}
        {result.average.toFixed(2)}
        {result.reportAverage !== null && (
          <> — the report says {result.reportAverage.toFixed(2)}{result.averageMatches ? ', which matches' : ', which does not match'}</>
        )}
        .
      </p>
      {clean && <p className="small muted">Every answer matched a question here and agreed with its option.</p>}
      {result.unmatched.length > 0 && (
        <>
          <p className="small strong">Not imported — no matching question here:</p>
          <ul className="small">
            {result.unmatched.map((u) => (
              <li key={u}>{u}</li>
            ))}
          </ul>
        </>
      )}
      {result.disagreements.length > 0 && (
        <>
          <p className="small strong">Imported by the letter, but the wording differs:</p>
          <ul className="small">
            {result.disagreements.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
}

function NewAssessment({ onClose, onCreated }: { onClose: () => void; onCreated: (id: number) => void }) {
  const today = new Date();
  const [title, setTitle] = useState(`OML Assessment ${today.getFullYear()}`);
  const [company, setCompany] = useState('');
  const [periodLabel, setPeriodLabel] = useState(`Q${Math.floor(today.getMonth() / 3) + 1} ${today.getFullYear()}`);
  const [conductedOn, setConductedOn] = useState(todayLocal());
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await api.post<{ AssessmentId: number }>('/assessments', {
        templateCode: 'OML',
        title,
        company: company.trim() || null,
        periodLabel,
        conductedOn,
        status: 'in_progress',
      });
      onCreated(created.AssessmentId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="New OML assessment"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={saving || !title.trim()} onClick={() => void submit()}>
            {saving ? 'Creating…' : 'Create'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <Field label="Company" hint="Which company was assessed — Compare over time draws one line per company.">
        <input
          className="input"
          value={company}
          autoFocus
          placeholder="The company that was assessed"
          onChange={(e) => setCompany(e.target.value)}
        />
      </Field>
      <Field label="Name">
        <input
          className="input"
          value={title}
          placeholder="e.g. Annual review, Post-acquisition baseline"
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>
      <Field label="Period">
        <input className="input" value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} />
      </Field>
      <Field label="Conducted on">
        <input className="input" type="date" value={conductedOn} onChange={(e) => setConductedOn(e.target.value)} />
      </Field>
    </Modal>
  );
}

/**
 * Name an assessment: its company, name and date — EOS Lite's "✎ Edit" on a
 * saved result, the same three fields in the same order, plus the period this
 * app also keeps.
 *
 * One synced from eos.cmittechs.com (lib/omlSync.ts) is EOS Lite's: the next
 * sync puts its name, company and date back to what is saved there, so the
 * form says so rather than letting a rename quietly disappear.
 */
function EditAssessment({
  assessment,
  onClose,
  onSaved,
}: {
  assessment: Pick<AssessmentSummary, 'AssessmentId' | 'Title' | 'Company' | 'PeriodLabel' | 'ConductedOn' | 'Synced'>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(assessment.Title);
  const [company, setCompany] = useState(assessment.Company ?? '');
  const [conductedOn, setConductedOn] = useState(assessment.ConductedOn?.slice(0, 10) ?? '');
  const [periodLabel, setPeriodLabel] = useState(assessment.PeriodLabel ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/assessments/${assessment.AssessmentId}`, {
        title: title.trim(),
        company: company.trim() || null,
        conductedOn: conductedOn || null,
        periodLabel: periodLabel.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Edit assessment"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={saving || !title.trim()} onClick={() => void submit()}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      {assessment.Synced && (
        <p className="small muted">
          This one came from eos.cmittechs.com. The next sync puts its company, name and date back to what is saved
          there — to keep a change, make it in eos.cmittechs.com too.
        </p>
      )}
      <Field label="Company" hint="Which company was assessed — Compare over time draws one line per company.">
        <input
          className="input"
          value={company}
          autoFocus
          maxLength={200}
          placeholder="The company that was assessed"
          onChange={(e) => setCompany(e.target.value)}
        />
      </Field>
      <Field label="Name">
        <input
          className="input"
          value={title}
          maxLength={200}
          placeholder="e.g. Annual review, Post-acquisition baseline"
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>
      <Field label="Assessment date">
        <input className="input" type="date" value={conductedOn} onChange={(e) => setConductedOn(e.target.value)} />
      </Field>
      <Field label="Period">
        <input className="input" value={periodLabel} placeholder="e.g. Q4 2026" onChange={(e) => setPeriodLabel(e.target.value)} />
      </Field>
    </Modal>
  );
}

type OmlTab = 'results' | 'playbook' | 'answers';

function AssessmentDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const resource = useResource<Assessment>(`/assessments/${id}`);
  const [error, setError] = useState<string | null>(null);
  /* Null means "the user has not chosen a tab yet", which is not the same as
     any particular tab: the right default depends on the assessment, and the
     assessment has not loaded when this runs. Declared up here with the other
     hooks — a hook behind an early return is a hook that sometimes does not
     run — and resolved below once the data is in hand. */
  const [tab, setTab] = useState<OmlTab | null>(null);
  const [editing, setEditing] = useState(false);

  /* Plain checks again. `useResource` now distinguishes a first load from a
     re-read of the same path, so `loading` is false while an answer's save
     reloads the averages — which is what stopped this page rebuilding itself
     on every click. Guarding on `!resource.data` here as well was a narrower
     fix for the same thing, and a worse one: opening a SECOND assessment is a
     new path with the previous one's answers still in state, and it would have
     shown those instead of a spinner. */
  if (resource.loading) return <Loading />;
  if (resource.error) return <ErrorBanner message={resource.error} />;
  if (!resource.data) return <Empty title="Assessment not found" />;

  const a = resource.data;
  const scored = a.dimensions.reduce((n, d) => n + d.scoredCount, 0);
  const total = a.dimensions.reduce((n, d) => n + d.criteriaCount, 0);

  /* A FINISHED assessment opens on its results, because that is what somebody
     opens a finished assessment to read. Anything else opens on the questions,
     because that is the work. Defaulting everything to Results — which the
     first version of this did — meant a new assessment opened on an empty
     chart and the whole scoring form looked like it had vanished. */
  const active: OmlTab = tab ?? (a.Status === 'complete' ? 'results' : 'answers');

  const saveScore = (criterionId: number, body: { score?: number | null; notes?: string | null }) => {
    /* Applied locally first so the answer lights up on the click rather than a
       round trip later. The reload that follows is only there for the numbers
       this cannot compute — the dimension averages and the weighted overall,
       which the API works out and which deliberately live in one place. */
    resource.set((current) => ({
      ...current,
      dimensions: current.dimensions.map((d) => ({
        ...d,
        criteria: d.criteria.map((c) =>
          c.CriterionId === criterionId
            ? { ...c, score: body.score ?? null, notes: body.notes ?? null }
            : c,
        ),
      })),
    }));

    return api
      .put(`/assessments/${id}/scores/${criterionId}`, {
        score: body.score ?? null,
        notes: body.notes ?? null,
      })
      .then(resource.reload)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Could not save');
        /* The optimistic update above is now a lie — the server never took it.
           Re-reading puts the page back to what was actually saved rather than
           leaving an answer showing as chosen when it is not. */
        resource.reload();
      });
  };

  return (
    <>
      <PageHead
        title={a.Title}
        lede={`${a.Company ? `${a.Company} · ` : ''}${a.PeriodLabel ?? 'No period set'} · conducted ${formatDate(a.ConductedOn)}`}
        actions={
          <>
            <button type="button" className="btn btn-ghost" onClick={onBack}>
              ← All assessments
            </button>
            <button type="button" className="btn btn-ghost" title="Edit the company, name or date" onClick={() => setEditing(true)}>
              Edit
            </button>
            <select
              className="select"
              style={{ width: 150 }}
              value={a.Status}
              onChange={(e) =>
                void api.put(`/assessments/${id}`, { status: e.target.value }).then(resource.reload)
              }
            >
              <option value="draft">Draft</option>
              <option value="in_progress">In progress</option>
              <option value="complete">Complete</option>
            </select>
          </>
        }
      />

      {error && <ErrorBanner message={error} />}

      {editing && (
        <EditAssessment
          assessment={a}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            resource.reload();
          }}
        />
      )}

      <OverallBanner score={a.overallScore} />

      <Tabs<OmlTab>
        tabs={[
          { key: 'results', label: 'Results' },
          { key: 'playbook', label: 'Proposed KPIs & Processes' },
          { key: 'answers', label: 'Answers', count: total },
        ]}
        active={active}
        onChange={setTab}
      />

      {active === 'results' && (
        <div className="stack">
          <ResultsBySection dimensions={a.dimensions} scored={scored} total={total} />
          <SummaryCard id={id} summary={a.Summary} onSaved={resource.reload} />
        </div>
      )}

      {active === 'playbook' && <Playbook assessmentId={id} scored={scored} />}

      {active === 'answers' && (
        <div className="stack">
        {a.dimensions.map((d) => (
          <section key={d.DimensionId} className="card">
            <div className="card-head">
              <div className="grow">
                <span className="eyebrow">Section</span>
                <h2>{d.Name}</h2>
                {d.DepartmentName && <p className="xs subtle">Scoring {d.DepartmentName}</p>}
                {d.Description && <p className="sub">{d.Description}</p>}
              </div>
              <div style={{ textAlign: 'right', minWidth: 120 }}>
                <div
                  className="mono"
                  style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, color: 'var(--text-strong)' }}
                >
                  {d.average === null ? '—' : d.average.toFixed(2)}
                </div>
                <div className="xs muted">{maturityLabel(d.average)}</div>
                <div className="mt-2">
                  <Meter value={d.average} />
                </div>
              </div>
            </div>

            <div className="card-body stack">
              {d.criteria.map((c) => (
                <CriterionRow key={c.CriterionId} criterion={c} onSave={saveScore} />
              ))}
            </div>
          </section>
        ))}

        </div>
      )}
    </>
  );
}

/** The wrap-up, on the Results tab because that is the page somebody reads
 *  when the assessment is finished — behind Answers it would never be seen. */
function SummaryCard({
  id,
  summary,
  onSaved,
}: {
  id: number;
  summary: string | null;
  onSaved: () => void;
}) {
  return (
    <section className="card">
      <div className="card-head">
        <div>
          <span className="eyebrow">Wrap up</span>
          <h2>Summary</h2>
          <p className="sub">What the scores mean and what happens next.</p>
        </div>
      </div>
      <div className="card-body">
        <InlineEdit
          value={summary}
          rows={5}
          placeholder="Findings, themes and the two or three things to fix first…"
          onSave={(next) => void api.put(`/assessments/${id}`, { summary: next }).then(onSaved)}
        />
      </div>
    </section>
  );
}

/**
 * The headline: one number for the whole business, and the level it sits at.
 *
 * Two figures rather than one, because they answer different questions. The
 * average is the instrument's own unit and the thing that moves between
 * quarters; the percentage is how the result gets said out loud in a
 * leadership meeting. Showing both stops somebody doing the division in their
 * head and getting it wrong.
 */
function OverallBanner({ score }: { score: number | null }) {
  const step = maturityToneClass(score);
  const pct = maturityPercent(score);

  return (
    <section className="card oml-overall mb-4">
      <div className="card-body">
        <div className="row-between wrap" style={{ gap: 'var(--space-4)' }}>
          <div>
            <span className="eyebrow">Company overall maturity</span>
            <div className="mt-2">
              <span className={`badge oml-badge ${step}`}>{maturityLabel(score)}</span>
            </div>
          </div>
          <div className="row" style={{ gap: 'var(--space-6)' }}>
            <div style={{ textAlign: 'right' }}>
              <div className="oml-figure">{score === null ? '—' : score.toFixed(2)}</div>
              <div className="xs muted">avg score (1–5)</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="oml-figure">{pct === null ? '—' : `${pct}%`}</div>
              <div className="xs muted">maturity score</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Every section's score as a meter, worst first.
 *
 * SECTIONS, NOT DEPARTMENTS, and the distinction is new. This card said
 * "department" throughout while the sections were really business units; since
 * migration 183 a section names the department it SCORES, and several sections
 * share one — five of the eleven score Engineering. So the row is the section,
 * which is the thing that has a score, and the department is printed beside it.
 *
 * WORST FIRST is the whole point of the ordering. Alphabetical would put the
 * section that needs attention wherever its initial happens to fall; this
 * puts it at the top, where a leadership team reading down the page meets it
 * before anything else. Unscored sections sink to the bottom — they are not
 * doing badly, they are simply not answered yet, and mixing the two would read
 * as a zero.
 *
 * The bar is a MAGNITUDE, so it is a plain proportional fill anchored to the
 * left, no axis and no gridlines: eleven rows against a common width compare
 * themselves. Colour is the app's status ladder — navy for a level 3 or
 * better, then the two accent steps down to critical — and it is never the
 * only encoding: the score, the percentage and the level name are printed on
 * every row, which is also what discharges the contrast warning the palette
 * validator raises on the middle step.
 */
function ResultsBySection({
  dimensions,
  scored,
  total,
}: {
  dimensions: Assessment['dimensions'];
  scored: number;
  total: number;
}) {
  /* Copied before sorting — sort() mutates, and this array belongs to the
     fetched assessment that the Answers tab renders in template order. */
  const rows = [...dimensions].sort((x, y) => {
    if (x.average === null) return y.average === null ? 0 : 1;
    if (y.average === null) return -1;
    return x.average - y.average;
  });

  return (
    <section className="card">
      <div className="card-head">
        <div className="grow">
          <span className="eyebrow">Results by section</span>
          {scored < total && (
            <p className="sub">
              {scored} of {total} answered — the scores below cover what has been answered so far.
            </p>
          )}
        </div>
      </div>
      <div className="card-body">
        {rows.length === 0 ? (
          <p className="muted small">This assessment has no sections.</p>
        ) : scored === 0 ? (
          <p className="muted small">
            Nothing has been answered yet, so there is nothing to chart. The questions are on
            the <strong>Answers</strong> tab.
          </p>
        ) : (
          <div className="oml-results">
            {rows.map((d) => {
              const step = maturityToneClass(d.average);
              const pct = maturityPercent(d.average);
              return (
                <div
                  key={d.DimensionId}
                  className="oml-result"
                  title={`${d.Name}${d.DepartmentName ? ` (${d.DepartmentName})` : ''} — ${
                    d.average === null ? 'not scored' : `${d.average.toFixed(2)} of 5`
                  } · ${d.scoredCount} of ${d.criteriaCount} answered`}
                >
                  <div className="oml-result-head">
                    <span className="oml-result-name">{d.Name}</span>
                    <span className="oml-result-figures">
                      <span className="mono">
                        {d.average === null ? '—' : `${d.average.toFixed(2)} · ${pct}%`}
                      </span>
                      <span className={`badge oml-badge ${step}`}>{maturityLabel(d.average)}</span>
                    </span>
                  </div>
                  <div className="oml-meter">
                    <div className={`oml-meter-fill ${step}`} style={{ width: `${pct ?? 0}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}

/** Unique across both catalogues — see `promote`. */
function processKey(p: PlaybookProcess): string {
  return p.Origin === 'library' ? `library:${p.ProcessId}` : `playbook:${p.PlaybookId}`;
}

/**
 * The path to the next level.
 *
 * Suggestions follow each section's own score rather than the overall
 * figure — a business is rarely uniformly immature, and the function sitting
 * at 1.4 needs different advice from the one at 3.6. Processes can be pushed
 * onto the V/TO issues list, because a suggestion that stays on this page is a
 * suggestion nobody does.
 *
 * THE PROCESSES ARE THE REAL ONES. They come out of the library the Processes
 * page maintains, filtered to the area its department maps onto and taken at
 * the OML level it is climbing to — so the plan proposes something that already exists, with
 * an owner and measurables against it, rather than a sentence describing
 * something like it. The seeded playbook wording only appears where the
 * library has nothing at that area and level. See the API handler.
 */
function Playbook({ assessmentId, scored }: { assessmentId: number; scored: number }) {
  const resource = useResource<PlaybookDimension[]>(`/assessments/${assessmentId}/playbook`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  if (scored === 0) {
    return (
      <section className="card">
        <div className="card-head">
          <div>
            <span className="eyebrow">Next level</span>
            <h2>Improvement plan</h2>
            <p className="sub">
              Score a few questions and the path to the next level appears here, per section.
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (resource.loading) return <Loading label="Working out the next level" />;
  const dimensions = resource.data ?? [];

  /* Two catalogues, two endpoints — see the API. The key is prefixed rather
     than being the bare id, because a library ProcessId and a PlaybookId are
     from different tables and collide at small numbers, which would spin the
     wrong row's button. */
  const promote = (process: PlaybookProcess) => {
    const key = processKey(process);
    const path =
      process.Origin === 'library'
        ? `/assessments/${assessmentId}/processes/${process.ProcessId}/promote`
        : `/assessments/${assessmentId}/playbook/${process.PlaybookId}/promote`;
    setBusy(key);
    setError(null);
    void api
      .post(path)
      .then(() => resource.reload())
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Could not add that to the issues list'),
      )
      .finally(() => setBusy(null));
  };

  return (
    <section className="card">
      <div className="card-head">
        <div className="grow">
          <span className="eyebrow">Next level</span>
          <h2>Improvement plan</h2>
          <p className="sub">
            The processes and measures each section needs for the level it is climbing to.
            Processes come from the library on the Processes page, taken at that level. Push one
            onto the issues list and it goes to the V/TO where the leadership team already works.
          </p>
        </div>
      </div>

      {error && (
        <div className="card-body tight">
          <ErrorBanner message={error} />
        </div>
      )}

      <div className="card-body stack">
        {dimensions.map((d) => (
          <div key={d.DimensionId} className="playbook-area">
            <div className="row-between wrap">
              <div>
                <h3>{d.Name}</h3>
                {d.DepartmentName && <p className="xs subtle">{d.DepartmentName}</p>}
                <p className="xs muted">
                  {d.Average === null
                    ? 'Not scored yet — start with the level 2 path'
                    : `Scoring ${d.Average.toFixed(1)} · ${d.ScoredCount} of ${d.CriteriaCount} answered`}
                </p>
              </div>
              {d.TargetLevel === null ? (
                <span className="badge badge-good">Already at level 5</span>
              ) : (
                <span className="badge badge-neutral">
                  Path to level {d.TargetLevel}
                </span>
              )}
            </div>

            {d.processes.length > 0 && (
              <div className="mt-4">
                <span className="eyebrow">Processes to put in place</span>
                <div className="stack mt-2" style={{ gap: 'var(--space-2)' }}>
                  {d.processes.map((p) => {
                    const key = processKey(p);
                    return (
                      <div key={key} className="mini-row">
                        <div className="grow">
                          <div className="strong small">{p.Title}</div>
                          {p.Detail && <div className="xs muted">{p.Detail}</div>}
                          {p.OwnerName && <div className="xs subtle">Owned by {p.OwnerName}</div>}
                        </div>
                        {/* The level the process itself sits at. Shown on the row
                            rather than only in the heading above, because these
                            rows get read out one at a time and "why this one"
                            is answered by the level. */}
                        <span
                          className="badge badge-neutral"
                          title={`Operational maturity level ${p.TargetLevel}`}
                        >
                          L{p.TargetLevel} {maturityName(p.TargetLevel)}
                        </span>
                        {p.IssueId ? (
                          <span className="badge badge-good">On the issues list</span>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={busy === key}
                            onClick={() => promote(p)}
                          >
                            {busy === key ? 'Adding…' : 'Add to Issues'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {d.kpis.length > 0 && (
              <div className="mt-4">
                <span className="eyebrow">Measures to start tracking</span>
                <div className="table-wrap mt-2">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>KPI</th>
                        <th style={{ width: 210 }}>Target at this level</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.kpis.map((k) => (
                        <tr key={k.PlaybookId}>
                          <td>
                            <div className="strong">{k.Title}</div>
                            {k.Detail && <div className="xs muted">{k.Detail}</div>}
                            {k.Source && <div className="xs subtle">Source: {k.Source}</div>}
                          </td>
                          <td className="mono small">{k.MetricTarget ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}


function CriterionRow({
  criterion,
  onSave,
}: {
  criterion: Criterion;
  onSave: (criterionId: number, body: { score?: number | null; notes?: string | null }) => void;
}) {
  /* LevelDescriptors is JSON authored in the seed. If it is malformed we fall
     back to the bare 1–5 buttons rather than taking the page down with it. */
  let levels: Record<string, string> | null = null;
  if (criterion.LevelDescriptors) {
    try {
      levels = JSON.parse(criterion.LevelDescriptors) as Record<string, string>;
    } catch {
      levels = null;
    }
  }

  const options = levels
    ? Object.entries(levels)
        .map(([level, text]) => ({ level: Number(level), text }))
        .filter((o) => Number.isFinite(o.level))
        .sort((x, y) => x.level - y.level)
    : [];

  return (
    <div style={{ borderBottom: '1px solid var(--border-subtle)', paddingBottom: 'var(--space-4)' }}>
      <div className="strong">{criterion.Text}</div>

      {options.length > 0 ? (
        /* THE ANSWERS, not a hint about the answers. These five sentences used
           to sit behind a "What does each level mean?" toggle with a row of
           numbered buttons beside it, so scoring meant opening the descriptions,
           reading them, closing them and then translating the one you picked
           into a digit. Seed 012 calls them what they are — "five answers
           ordered least to most mature" — so they are the thing you click. */
        <div
          className="oml-answers mt-2"
          role="radiogroup"
          aria-label={criterion.Text}
        >
          {options.map((o) => {
            const chosen = criterion.score === o.level;
            return (
              <button
                key={o.level}
                type="button"
                role="radio"
                aria-checked={chosen}
                className={`oml-answer${chosen ? ' is-chosen' : ''}`}
                /* Clicking the chosen answer again clears it — otherwise a
                   mis-click is permanent, since there is no other way to put a
                   question back to unanswered. */
                onClick={() =>
                  onSave(criterion.CriterionId, {
                    score: chosen ? null : o.level,
                    notes: criterion.notes,
                  })
                }
              >
                <span className="oml-answer-level mono">{o.level}</span>
                <span>{o.text}</span>
              </button>
            );
          })}
        </div>
      ) : (
        /* No descriptors authored for this question — the numbers are all
           there is to offer. */
        <div className="mt-2">
          <ScaleInput
            value={criterion.score}
            onChange={(score) => onSave(criterion.CriterionId, { score, notes: criterion.notes })}
          />
        </div>
      )}

      <div className="mt-2">
        <InlineEdit
          value={criterion.notes}
          rows={1}
          placeholder="Evidence or notes…"
          className="inline-edit small"
          onSave={(notes) => onSave(criterion.CriterionId, { score: criterion.score, notes })}
        />
      </div>
    </div>
  );
}
